import { Moon, Sun } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/utils/cn'
import { useTheme } from '@/contexts/ThemeContext'

/** Light/dark toggle. Reads the resolved theme and flips the `.dark` class
 *  (handled by ThemeContext). Styled to match the garage glass header.
 *  - `icon` (default): compact square button for the header bar.
 *  - `row`: full-width labeled row with a sliding switch, for the mobile menu. */
export default function ThemeToggle({
  className = '',
  variant = 'icon',
}: {
  className?: string
  variant?: 'icon' | 'row' | 'bare'
}) {
  const { resolvedTheme, toggleTheme } = useTheme()
  const { t } = useTranslation('nav')
  const isDark = resolvedTheme === 'dark'
  const label = isDark ? t('theme.light') : t('theme.dark')

  if (variant === 'row') {
    return (
      <button
        type="button"
        onClick={toggleTheme}
        aria-label={label}
        className={cn(
          'flex w-full items-center justify-between gap-3 px-4 py-3 rounded-lg bg-garage-card/40 backdrop-blur-sm border border-garage-accent/20 text-garage-cream hover:bg-garage-card/60 hover:border-garage-accent/40 transition-all duration-300',
          className
        )}
      >
        <span className="flex items-center gap-3 text-base font-medium">
          {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          {label}
        </span>
        {/* iOS-style switch — knob slides to the right in dark mode */}
        <span
          className={cn(
            'relative h-6 w-11 shrink-0 rounded-full transition-colors duration-300',
            isDark ? 'bg-garage-accent/80' : 'bg-garage-cream/20'
          )}
        >
          <span
            className={cn(
              'absolute top-0.5 h-5 w-5 rounded-full bg-garage-cream shadow transition-transform duration-300',
              isDark ? 'translate-x-[22px]' : 'translate-x-0.5'
            )}
          />
        </span>
      </button>
    )
  }

  // Tiny dim icon — matches the compact language-code row.
  if (variant === 'bare') {
    return (
      <button
        type="button"
        onClick={toggleTheme}
        aria-label={label}
        title={label}
        className={cn(
          'relative grid place-items-center p-1 rounded text-garage-cream/40 hover:text-garage-cream/80 transition-colors duration-300',
          className
        )}
      >
        {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </button>
    )
  }

  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label={label}
      title={label}
      className={cn(
        'relative grid place-items-center p-3 rounded-lg bg-garage-card/40 backdrop-blur-sm border border-garage-accent/20 text-garage-cream hover:bg-garage-card/60 hover:border-garage-accent/40 transition-all duration-300',
        className
      )}
    >
      {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
    </button>
  )
}
